import { useParams, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, ArrowUpRight, MapPin, RefreshCw, Clock, Users2, FileText, Target } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { services } from '@/data/services';
import { IMAGE_WIDTHS, buildSrc, buildSrcSet } from '@/lib/responsiveImage';
import { HARZ_CITIES } from '@/data/harzCities';
import { generalContact, decodeContactInfo } from '@/data/contacts';

const contactPhone = decodeContactInfo(generalContact.phone);

const webdesignImg = '/images/services/webdesign';
const karriereseitenImg = '/images/services/karriereseiten';
const hostingImg = '/images/services/hosting';
const seoImg = '/images/services/seo';
const bookingImg = '/images/services/booking';
const beratungImg = '/images/services/beratung';
const webseitencheckImg = '/images/services/webseitencheck';
const seaImg = '/images/services/sea';
const geoImg = '/images/services/geo';
const kiBeratungImg = '/images/services/ki-beratung';

const serviceImages: Record<string, { main: string; secondary?: string }> = {
  'ki-beratung': { main: kiBeratungImg, secondary: beratungImg },
  'webseitenerstellung': { main: webdesignImg, secondary: beratungImg },
  'karriereseiten': { main: karriereseitenImg, secondary: webdesignImg },
  'hosting-domains': { main: hostingImg, secondary: seoImg },
  'seo-optimierung': { main: seoImg, secondary: webdesignImg },
  'buchungssysteme': { main: bookingImg, secondary: beratungImg },
  'digitalisierung-beratung': { main: beratungImg, secondary: hostingImg },
  'webseitencheck': { main: webseitencheckImg, secondary: hostingImg },
  'sea': { main: seaImg, secondary: hostingImg },
  'geo': { main: geoImg, secondary: hostingImg },
};

const LeistungDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const service = services.find((s) => s.slug === slug);

  if (!service) {
    return <Navigate to="/leistungen" replace />;
  }

  // Webseitencheck has its own dedicated page
  if (service.link) {
    return <Navigate to={service.link} replace />;
  }

  const images = serviceImages[service.slug] || { main: webdesignImg };
  const imageSizes = '(min-width: 1024px) 50vw, 100vw';

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main id="main-content" tabIndex={-1}>
      
      {/* Hero Section - Dark */}
      <section className="pt-32 pb-20 relative section-dark">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center mb-6 mx-auto`}>
              <service.icon className={`w-10 h-10 ${service.iconColor}`} aria-hidden="true" />
            </div>
            <span className="text-primary font-medium text-sm uppercase tracking-wider">{service.title}</span>
            <h1 className="text-4xl md:text-6xl font-display font-bold mt-4 mb-6 text-white">
              {service.heroTitle}
            </h1>
            <p className="text-white/80 text-lg md:text-xl">
              {service.heroDescription}
            </p>
          </motion.div>
        </div>
      </section>

      {/* GEO: Answer-First & Key Facts */}
      {(service.answerFirst || (service.keyFacts && service.keyFacts.length > 0)) && (
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto grid gap-8 lg:grid-cols-5">
              {service.answerFirst && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="lg:col-span-3 bg-card border border-border rounded-2xl p-8 shadow-lg"
                >
                  <span className="text-primary font-medium text-sm uppercase tracking-wider">Kurz gesagt</span>
                  <p className="mt-3 text-lg leading-relaxed text-card-foreground hyphens-auto" lang="de">
                    {service.answerFirst}
                  </p>
                </motion.div>
              )}
              {service.keyFacts && service.keyFacts.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="lg:col-span-2 bg-primary/5 border border-primary/20 rounded-2xl p-8"
                >
                  <span className="text-primary font-medium text-sm uppercase tracking-wider">Kernpunkte</span>
                  <ul className="mt-3 space-y-2">
                    {service.keyFacts.map((fact) => (
                      <li key={fact} className="flex items-start gap-2 text-sm text-foreground">
                        <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="hyphens-auto" lang="de">{fact}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Main Content with Image */}
      <section className="py-20 bg-background">

        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 text-foreground">
                {service.title}
              </h2>
              <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                {service.seoText}
              </p>
              <ul className="grid sm:grid-cols-2 gap-3">
                {service.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-primary" aria-hidden="true" />
                    </div>
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src={buildSrc(images.main, 800)} 
                  srcSet={buildSrcSet(images.main, IMAGE_WIDTHS.card)}
                  sizes={imageSizes}
                  alt={service.title}
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={570}
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Zielgruppe */}
      {service.audience && (
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <span className="text-primary font-medium text-sm uppercase tracking-wider">Zielgruppe</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold mt-4 mb-5 text-foreground">
                Zielgruppe
              </h2>
              <p className="text-muted-foreground text-lg hyphens-auto" lang="de">{service.audience.intro}</p>
              <ul className="flex flex-wrap justify-center gap-3 mt-8">
                {service.audience.groups.map((group) => (
                  <li
                    key={group}
                    className="px-5 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm font-medium text-foreground"
                  >
                    {group}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Pakete */}
      {service.packages && service.packages.length > 0 && (
        <section className="py-20 section-dark">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-14"
            >
              <span className="text-primary font-medium text-sm uppercase tracking-wider">Schulungsvarianten</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold mt-4 mb-5 text-white">
                Unsere Pakete
              </h2>
              <p className="text-white/80 text-lg">
                Drei Formate, ein Ziel: Ihr Team nutzt KI sicher und sinnvoll. Wir empfehlen Ihnen nach einem kurzen
                Vorgespräch das passende Format – mit transparentem Festpreis im Angebot.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-6 items-stretch">
              {service.packages.map((pkg, index) => (
                <motion.div
                  key={pkg.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative flex flex-col h-full rounded-2xl p-8 shadow-lg border ${
                    pkg.recommended
                      ? 'bg-card border-primary/60 ring-1 ring-primary/30'
                      : 'bg-card border-border'
                  }`}
                >
                  {pkg.recommended && (
                    <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold uppercase tracking-wider">
                      Empfehlung
                    </span>
                  )}
                  <h3 className="text-2xl font-display font-bold text-card-foreground">{pkg.name}</h3>
                  <p className="text-sm text-primary font-medium mt-1 hyphens-auto" lang="de">{pkg.subtitle}</p>
                  <p className="inline-flex items-center gap-2 text-sm text-muted-foreground mt-4">
                    <Clock className="w-4 h-4 text-primary" aria-hidden="true" />
                    {pkg.duration}
                  </p>
                  <ul className="space-y-2 mt-6 flex-1">
                    {pkg.items.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-foreground">
                        <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="hyphens-auto" lang="de">{item}</span>
                      </li>
                    ))}
                  </ul>
                  {pkg.suitedFor && (
                    <p className="text-sm text-muted-foreground mt-6 pt-4 border-t border-border hyphens-auto" lang="de">
                      {pkg.suitedFor}
                    </p>
                  )}
                  <Link
                    to="/kontakt"
                    className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg"
                  >
                    Preis anfragen
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Ablauf */}
      {service.agenda && service.agenda.items.length > 0 && (
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <span className="text-primary font-medium text-sm uppercase tracking-wider">Beispielablauf</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold mt-4 mb-5 text-foreground">
                Beispielablauf
              </h2>
              <p className="text-muted-foreground text-lg mb-8 hyphens-auto" lang="de">{service.agenda.intro}</p>
              <ol className="space-y-3">
                {service.agenda.items.map((item) => (
                  <li
                    key={item.time}
                    className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-6 bg-card border border-border rounded-xl px-5 py-4 shadow-sm"
                  >
                    <span className="text-sm font-semibold text-primary tabular-nums whitespace-nowrap">{item.time}</span>
                    <span className="text-foreground hyphens-auto" lang="de">{item.title}</span>
                  </li>
                ))}
              </ol>
              {service.agenda.note && (
                <p className="text-sm text-muted-foreground mt-6 italic hyphens-auto" lang="de">{service.agenda.note}</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Lernziele */}
      {service.learningGoals && service.learningGoals.length > 0 && (
        <section className="py-20 section-dark">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center mb-5 mx-auto">
                  <Target className="w-7 h-7 text-primary" aria-hidden="true" />
                </div>
                <span className="text-primary font-medium text-sm uppercase tracking-wider">Lernziele</span>
                <h2 className="text-3xl md:text-4xl font-display font-bold mt-4 text-white">
                  Lernziele
                </h2>
              </div>
              <ul className="grid sm:grid-cols-2 gap-4">
                {service.learningGoals.map((goal) => (
                  <li
                    key={goal}
                    className="flex items-start gap-3 bg-card border border-border rounded-xl p-4 shadow-sm"
                  >
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span className="text-sm text-card-foreground hyphens-auto" lang="de">{goal}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Leistungsumfang / Unterlagen */}
      {service.deliverables && service.deliverables.length > 0 && (
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center max-w-3xl mx-auto mb-12">
                <span className="text-primary font-medium text-sm uppercase tracking-wider">Im Preis enthalten</span>
                <h2 className="text-3xl md:text-4xl font-display font-bold mt-4 mb-5 text-foreground">
                  Im Preis enthalten
                </h2>
                <p className="text-muted-foreground text-lg">
                  Alle Materialien sind Bestandteil der Schulung und werden vor Ort bzw. digital an die Teilnehmenden
                  übergeben.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {service.deliverables.map((item, index) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-card border border-border rounded-2xl p-6 shadow-sm h-full"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                        {index === service.deliverables!.length - 1 ? (
                          <Users2 className="w-5 h-5 text-primary" aria-hidden="true" />
                        ) : (
                          <FileText className="w-5 h-5 text-primary" aria-hidden="true" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-display font-semibold text-card-foreground mb-1 hyphens-auto" lang="de">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground hyphens-auto" lang="de">{item.description}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Process Section */}

      <section className="py-20 section-dark">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="text-primary font-medium text-sm uppercase tracking-wider">Unser Vorgehen</span>
            <h2 className="text-3xl md:text-4xl font-display font-bold mt-4 mb-6 text-white">
              Unser Vorgehen
            </h2>
            <p className="text-white/80 text-lg">
              Ein strukturierter Prozess für Ihren Erfolg – transparent und effizient.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {service.process.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-card rounded-2xl p-6 border border-border shadow-lg h-full">
                  <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center mb-4">
                    <span className="text-xl font-bold text-primary">{step.step}</span>
                  </div>
                  <h3 className="text-lg font-display font-semibold mb-3 text-card-foreground">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {step.description}
                  </p>
                </div>
                {index < service.process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <ArrowRight className="w-6 h-6 text-primary/50" aria-hidden="true" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {images.secondary && (
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="order-2 lg:order-1"
              >
                <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src={buildSrc(images.secondary, 800)} 
                    srcSet={buildSrcSet(images.secondary, IMAGE_WIDTHS.card)}
                    sizes={imageSizes}
                    alt={`${service.title} Vorteile`}
                    loading="lazy"
                    decoding="async"
                    width={800}
                    height={570}
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>
            )}
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className={`order-1 lg:order-2 ${!images.secondary ? 'lg:col-span-2 max-w-3xl mx-auto' : ''}`}
            >
              <span className="text-primary font-medium text-sm uppercase tracking-wider">Ihre Vorteile</span>
              <h2 className="text-3xl md:text-4xl font-display font-bold mt-4 mb-6 text-foreground">
                Ihre Vorteile
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                {service.benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="group bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-4 border border-primary/20 hover:border-primary/40 transition-all duration-300"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary/30 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/40 transition-colors">
                        <Check className="w-4 h-4 text-primary" aria-hidden="true" />
                      </div>
                      <span className="text-sm font-medium text-foreground">{benefit}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tools Section (if available) */}
      {service.tools && service.tools.length > 0 && (
        <section className="py-16 section-dark">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <span className="text-primary font-medium text-sm uppercase tracking-wider">Technologien & Tools</span>
              <h3 className="text-2xl font-display font-bold mt-4 mb-8 text-white">
                Tools und Technologien
              </h3>
              <div className="flex flex-wrap justify-center gap-4">
                {service.tools.map((tool) => (
                  <span 
                    key={tool}
                    className="px-6 py-3 bg-card rounded-full border border-border text-card-foreground font-medium"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* FAQ Section (if available) */}
      {service.faq && service.faq.length > 0 && (
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto"
            >
              <h2 className="text-2xl md:text-3xl font-display font-bold mb-8 text-foreground text-center">
                Häufige Fragen
              </h2>
              <div className="space-y-4">
                {service.faq.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-card rounded-xl p-6 border border-border shadow-lg"
                  >
                    <h3 className="font-semibold text-card-foreground mb-2">{item.question}</h3>
                    <p className="text-muted-foreground">{item.answer}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Regional Webdesign Pages - only for webseitenerstellung */}
      {service.slug === 'webseitenerstellung' && (
        <>
          <section className="py-20 bg-background">
            <div className="container mx-auto px-6">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="max-w-4xl mx-auto mb-12"
              >
                <div className="rounded-3xl border border-border bg-card p-8 shadow-lg">
                  <span className="text-primary font-medium text-sm uppercase tracking-wider">Rechtssichere Website-Basis</span>
                  <h2 className="text-3xl md:text-4xl font-display font-bold mt-4 mb-5 text-foreground">
                    eRecht24 ist Teil unserer Webseiten-Projekte
                  </h2>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-5">
                    Bei neuen Websites und Relaunches denken wir rechtliche Pflichtseiten direkt mit. Über unsere
                    eRecht24-Premiumpartnerschaft unterstützen wir Sie bei rechtssicherem Impressum,
                    rechtssicherer Datenschutzerklärung, dem richtigen Umgang mit Bildnachweisen und dem sauberen
                    Auflisten externer Dienste auf Ihrer Website.
                  </p>
                  <p className="text-muted-foreground text-lg leading-relaxed mb-7">
                    Das ist wichtig, weil fehlende oder fehlerhafte Angaben unnötige Risiken und im Zweifel auch
                    Abmahnungen nach sich ziehen können.
                  </p>
                  <Link
                    to="/netzwerk/erecht24"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg"
                  >
                    Mehr zur eRecht24-Partnerschaft
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </section>

          <section className="py-20 bg-background">
            <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-3xl mx-auto mb-12"
            >
              <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center mb-6 mx-auto">
                <MapPin className="w-8 h-8 text-primary" aria-hidden="true" />
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-4 text-foreground">
                Webdesign in Ihrer Stadt
              </h2>
              <p className="text-muted-foreground text-lg">
                Wir betreuen Unternehmen in 20 Harz-Städten. Finden Sie Ihre lokale Webdesign-Seite mit spezifischen Informationen für Ihre Region.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 border border-border shadow-lg"
            >
              <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {HARZ_CITIES.map((city) => (
                  <li key={city.slug}>
                    <Link 
                      to={`/webdesign/${city.slug}`} 
                      className="text-sm text-muted-foreground hover:text-primary transition-colors block p-3 rounded-lg hover:bg-primary/10 text-center"
                    >
                      {city.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
            </div>
          </section>
        </>
      )}
      {/* Add-ons & wiederkehrendes Update */}
      {(service.addOns?.length || service.recurring) && (
        <section className="py-20 bg-background">
          <div className="container mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto items-start">
              {service.addOns && service.addOns.length > 0 && (
                <div className="bg-card border border-border rounded-2xl p-8 shadow-lg h-full">
                  <span className="text-primary font-medium text-sm uppercase tracking-wider">Optional buchbar</span>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mt-3 mb-5 text-card-foreground">
                    Zusatzleistungen
                  </h2>
                  <ul className="space-y-3">
                    {service.addOns.map((addOn) => (
                      <li key={addOn} className="flex items-start gap-3 text-foreground">
                        <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="hyphens-auto" lang="de">{addOn}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {service.recurring && (
                <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 h-full">
                  <span className="text-primary font-medium text-sm uppercase tracking-wider">Jährlich</span>
                  <h2 className="text-2xl md:text-3xl font-display font-bold mt-3 mb-2 text-foreground">
                    {service.recurring.title}
                  </h2>
                  <p className="text-sm font-medium text-primary mb-4">{service.recurring.duration}</p>
                  <p className="text-muted-foreground mb-5 hyphens-auto" lang="de">{service.recurring.intro}</p>
                  <ul className="space-y-2 mb-6">
                    {service.recurring.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm text-foreground">
                        <RefreshCw className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                        <span className="hyphens-auto" lang="de">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-foreground font-display text-lg italic">„{service.recurring.claim}“</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Praxisbeispiel */}
      {service.exampleReference && (
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto bg-card border border-border rounded-2xl p-8 shadow-lg">
              <span className="text-primary font-medium text-sm uppercase tracking-wider">Beispiel aus der Praxis</span>
              <h2 className="text-xl md:text-2xl font-display font-bold mt-3 mb-3 text-card-foreground hyphens-auto" lang="de">
                {service.exampleReference.title}
              </h2>
              <p className="text-muted-foreground mb-6 hyphens-auto" lang="de">{service.exampleReference.text}</p>
              <Link
                to={`/referenzen/${service.exampleReference.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg"
              >
                Referenz ansehen
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Weiterführender Ratgeberartikel */}
      {service.relatedInsight && (
        <section className="py-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto bg-card border border-border rounded-2xl p-8 shadow-lg">
              <span className="text-primary font-medium text-sm uppercase tracking-wider">Zum Weiterlesen</span>
              <h2 className="text-xl md:text-2xl font-display font-bold mt-3 mb-3 text-card-foreground hyphens-auto" lang="de">
                {service.relatedInsight.title}
              </h2>
              <p className="text-muted-foreground mb-6 hyphens-auto" lang="de">{service.relatedInsight.teaser}</p>
              <Link
                to={`/ratgeber/${service.relatedInsight.slug}`}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg"
              >
                Ratgeber lesen
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Rechtlicher Hinweis */}
      {service.legalNote && (
        <section className="pb-16 bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto rounded-2xl border border-border bg-muted/40 p-6">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2">
                Rechtlicher Hinweis
              </h2>
              <p className="text-sm text-muted-foreground hyphens-auto" lang="de">{service.legalNote}</p>
            </div>
          </div>
        </section>
      )}

      <section className="py-20 section-dark">

        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-4 text-white">
              Weitere Leistungen
            </h2>
            <p className="text-white/80">
              Entdecken Sie unser komplettes Leistungsportfolio.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {services
              .filter((s) => s.slug !== service.slug)
              .map((s, index) => (
                <motion.div
                  key={s.slug}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={s.link || `/leistungen/${s.slug}`}
                    className="group block bg-card rounded-xl p-6 border border-border hover:border-primary/50 transition-all duration-300 h-full"
                  >
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-4`}>
                      <s.icon className={`w-6 h-6 ${s.iconColor}`} />
                    </div>
                    <h3 className="font-semibold text-card-foreground group-hover:text-primary transition-colors flex items-center gap-2 text-sm">
                      {s.title}
                      <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </h3>
                  </Link>
                </motion.div>
              ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 text-foreground">
              Interesse an {service.title}?
            </h2>
            <p className="text-muted-foreground text-lg mb-8">
              Lassen Sie uns gemeinsam Ihr Projekt besprechen. Das Erstgespräch ist kostenlos und unverbindlich.
            </p>
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-primary to-glow-secondary text-primary-foreground font-semibold hover-glow shadow-lg"
            >
              Kostenloses Erstgespräch vereinbaren
              <ArrowRight className="w-5 h-5" aria-hidden="true" />
            </Link>
          </motion.div>
        </div>
      </section>

      </main>
      <Footer />
    </div>
  );
};

export default LeistungDetailPage;
